import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { API_URL, API_BASE_URL } from "../config/api";
import {
  Download, Settings, AlertTriangle, RefreshCw, Search, X,
  Edit3, CheckCircle2,
} from "lucide-react";

/**
 * LCLSalesTariffBuilder
 * ---------------------
 * Turns the latest Freightlinks LCL import card into a Europacific
 * selling tariff. Surfaces the FL buy rate + markup → sell rate,
 * competitor cross-check (ECU / Shipco-SSCSI), flag-for-checking
 * indicator, and per-port-pair staff overrides. Exports a branded
 * XLSX with NO Freightlinks references for sending to customers.
 */
export default function LCLSalesTariffBuilder({ mode = "import" } = {}) {
  // mode: "import" (Koper = dest) or "export" (Koper = origin).
  // The component uses the same row schema, just with different endpoints
  // and a few different column labels. Import surfaces TT-to-SIN + buffer;
  // export surfaces a single TT value and uses POD/destination ports.
  const isExport = mode === "export";
  const endpointBase = isExport ? "lcl-export-sales-tariff" : "lcl-sales-tariff";
  const chargesField = isExport ? "origin_charges" : "destination_charges";
  const chargesLabel = isExport ? "Origin Charges (Koper)" : "Destination Charges (Koper)";
  const downloadName = isExport ? "Europacific_LCL_Export_Tariff" : "Europacific_LCL_Import_Tariff";
  const titleText = isExport ? "LCL Export Sales Tariff" : "LCL Import Sales Tariff";
  const portFieldKey = isExport ? "pod" : "pol";
  const portNameKey = isExport ? "pod_name" : "pol_name";
  const portLabel = isExport ? "Destination (POD)" : "Origin (POL)";

  const token = localStorage.getItem("token");
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [data, setData] = useState({ rows: [], card: null, config: null, summary: null });
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all"); // all | flagged | overridden
  const [showSettings, setShowSettings] = useState(false);
  const [settingsForm, setSettingsForm] = useState({
    default_markup_usd: isExport ? 30 : 25,
    transit_buffer_days: 45,
    flag_threshold_pct: 0,
    validity_start: "",
    validity_end: "",
    [chargesField]: [],
  });
  const [editingRow, setEditingRow] = useState(null);
  const [editForm, setEditForm] = useState({
    markup_usd: "", override_sell_rate: "", reason: "",
    surcharge_code: "", surcharge_amount: "", surcharge_basis: "per_set",
  });

  // ── Fetch builder data ─────────────────────────────────────────
  const fetchBuilder = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/${endpointBase}/builder`, { headers });
      setData(res.data || { rows: [], card: null, config: null });
      if (res.data?.config) {
        setSettingsForm({
          default_markup_usd: res.data.config.default_markup_usd ?? (isExport ? 30 : 25),
          transit_buffer_days: res.data.config.transit_buffer_days ?? 45,
          flag_threshold_pct: res.data.config.flag_threshold_pct ?? 0,
          validity_start: res.data.config.validity_start || "",
          validity_end: res.data.config.validity_end || "",
          [chargesField]: res.data.config[chargesField] || [],
        });
      }
    } catch (e) {
      console.error("sales tariff fetch error", e);
      toast.error(e.response?.data?.detail || "Failed to load tariff");
    } finally {
      setLoading(false);
    }
  }, [headers, endpointBase, isExport, chargesField]);

  useEffect(() => { fetchBuilder(); }, [fetchBuilder]);

  // ── Save config ───────────────────────────────────────────────
  const saveConfig = async () => {
    try {
      const body = {
        default_markup_usd: Number(settingsForm.default_markup_usd) || 0,
        flag_threshold_pct: Number(settingsForm.flag_threshold_pct) || 0,
        validity_start: settingsForm.validity_start || null,
        validity_end: settingsForm.validity_end || null,
      };
      if (!isExport) {
        body.transit_buffer_days = Number(settingsForm.transit_buffer_days) || 0;
      }
      body[chargesField] = (settingsForm[chargesField] || []).map(c => ({
        code: c.code || "",
        label: c.label || "",
        amount: Number(c.amount) || 0,
        currency: c.currency || "EUR",
        basis: c.basis || "per_shipment",
      }));
      await axios.put(`${API_URL}/${endpointBase}/config`, body, { headers });
      toast.success("Tariff settings saved");
      setShowSettings(false);
      fetchBuilder();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save settings");
    }
  };

  // ── Override save / clear ─────────────────────────────────────
  const saveOverride = async (row) => {
    const markup = editForm.markup_usd !== "" ? Number(editForm.markup_usd) : null;
    const absolute = editForm.override_sell_rate !== "" ? Number(editForm.override_sell_rate) : null;
    // Optional inline single-surcharge entry (BAT/DGS/TRD etc). Staff can
    // chain multiple by editing the row again — we merge rather than wipe.
    const newSurcharge = (editForm.surcharge_code && editForm.surcharge_amount)
      ? [{
          code: editForm.surcharge_code.trim().toUpperCase(),
          amount: Number(editForm.surcharge_amount) || 0,
          currency: "USD",
          basis: editForm.surcharge_basis || "per_set",
        }]
      : [];
    if (markup === null && absolute === null && newSurcharge.length === 0) {
      toast.error("Enter a markup, sell rate, or a surcharge");
      return;
    }
    try {
      const existing = (row.port_surcharges || []).filter(
        s => !newSurcharge.length || s.code !== newSurcharge[0].code
      );
      await axios.post(`${API_URL}/${endpointBase}/overrides`, {
        pol: row.pol,
        pod: row.pod,
        markup_usd: markup,
        override_sell_rate: absolute,
        reason: editForm.reason || null,
        surcharges: [...existing, ...newSurcharge],
      }, { headers });
      toast.success(`Saved ${row[portNameKey]}`);
      setEditingRow(null);
      setEditForm({
        markup_usd: "", override_sell_rate: "", reason: "",
        surcharge_code: "", surcharge_amount: "", surcharge_basis: "per_set",
      });
      fetchBuilder();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save override");
    }
  };

  const matchCompetition = async (row) => {
    if (!row.cheapest_competitor_rate) return;
    try {
      await axios.post(`${API_URL}/${endpointBase}/overrides`, {
        pol: row.pol,
        pod: row.pod,
        override_sell_rate: row.cheapest_competitor_rate,
        reason: `Matched ${row.cheapest_competitor} (${row.cheapest_competitor_rate} USD)`,
      }, { headers });
      toast.success(`${row[portNameKey]}: matched ${row.cheapest_competitor}`);
      fetchBuilder();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to match competition");
    }
  };

  const clearOverride = async (row) => {
    try {
      await axios.delete(`${API_URL}/${endpointBase}/overrides/${row.pol}/${row.pod}`, { headers });
      toast.success(`Override cleared for ${row[portNameKey]}`);
      fetchBuilder();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to clear override");
    }
  };

  // ── Charges helpers (origin OR destination depending on mode) ──
  const updateDestCharge = (idx, field, value) => {
    setSettingsForm(s => {
      const next = [...(s[chargesField] || [])];
      next[idx] = { ...next[idx], [field]: value };
      return { ...s, [chargesField]: next };
    });
  };
  const removeDestCharge = (idx) => {
    setSettingsForm(s => ({
      ...s,
      [chargesField]: (s[chargesField] || []).filter((_, i) => i !== idx),
    }));
  };

  // ── XLSX export (download blob) ───────────────────────────────
  const exportXlsx = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API_URL}/${endpointBase}/export.xlsx`, {
        headers,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${downloadName}_${new Date().toISOString().slice(0, 10).replace(/-/g, "")}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Tariff exported (no Freightlinks references)");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  // ── Filtering ─────────────────────────────────────────────────
  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (data.rows || []).filter((r) => {
      if (filter === "flagged" && !r.flagged_for_check) return false;
      if (filter === "overridden" && !r.has_override) return false;
      if (q) {
        const hay = `${r.country} ${r[portNameKey] || ""} ${r[portFieldKey] || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [data.rows, filter, search, portNameKey, portFieldKey]);

  // ── Render ────────────────────────────────────────────────────
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="lcl-sales-tariff-builder">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">
            {titleText}
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            Source: {data.card?.name || "—"} · {data.summary?.total_rows || 0} routes
            {" · "}<span className="text-amber-700">{data.summary?.flagged || 0} flagged</span>
            {" · "}<span className="text-emerald-700">{data.summary?.overridden || 0} overridden</span>
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowSettings((v) => !v)}
            data-testid="tariff-settings-btn"
          >
            <Settings className="h-4 w-4 mr-2" />
            Settings
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchBuilder}
            disabled={loading}
            data-testid="tariff-refresh-btn"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <Button
            size="sm"
            onClick={exportXlsx}
            disabled={exporting || !data.rows?.length}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="tariff-export-btn"
          >
            <Download className="h-4 w-4 mr-2" />
            {exporting ? "Exporting…" : "Export XLSX (Europacific-branded)"}
          </Button>
        </div>
      </div>

      {/* Settings panel */}
      {showSettings && (
        <div className="bg-slate-50 rounded-lg p-4 mb-5 border border-slate-200" data-testid="tariff-settings-panel">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <div>
              <Label className="text-xs">Default markup (USD/CBM)</Label>
              <Input
                type="number"
                step="0.5"
                value={settingsForm.default_markup_usd}
                onChange={(e) => setSettingsForm({ ...settingsForm, default_markup_usd: e.target.value })}
                data-testid="tariff-markup-input"
              />
            </div>
            {!isExport && (
              <div>
                <Label className="text-xs">Transit buffer (days)</Label>
                <Input
                  type="number"
                  value={settingsForm.transit_buffer_days}
                  onChange={(e) => setSettingsForm({ ...settingsForm, transit_buffer_days: e.target.value })}
                  data-testid="tariff-buffer-input"
                  title="Added to TT-to-SIN for routes via SGP. Default 45d."
                />
              </div>
            )}
            <div>
              <Label className="text-xs">Flag threshold (% above competitor)</Label>
              <Input
                type="number"
                step="1"
                value={settingsForm.flag_threshold_pct}
                onChange={(e) => setSettingsForm({ ...settingsForm, flag_threshold_pct: e.target.value })}
                data-testid="tariff-threshold-input"
                title="0 = flag whenever any competitor is cheaper"
              />
            </div>
            <div>
              <Label className="text-xs">Valid from</Label>
              <Input
                type="date"
                value={settingsForm.validity_start}
                onChange={(e) => setSettingsForm({ ...settingsForm, validity_start: e.target.value })}
              />
            </div>
            <div>
              <Label className="text-xs">Valid until</Label>
              <Input
                type="date"
                value={settingsForm.validity_end}
                onChange={(e) => setSettingsForm({ ...settingsForm, validity_end: e.target.value })}
              />
            </div>
          </div>
          <div className="mt-3 flex items-center gap-2">
            <Button size="sm" onClick={saveConfig} data-testid="tariff-save-config-btn" className="bg-emerald-600 hover:bg-emerald-700">
              Save settings
            </Button>
            <Button size="sm" variant="outline" onClick={() => setShowSettings(false)}>Cancel</Button>
          </div>

          {/* Destination Charges editor — appears at the bottom of the
              Calculator sheet in the exported XLSX. Defaults to a Koper
              LCL imports baseline; staff can edit any line or add new
              charges (e.g. fumigation, isps). */}
          <div className="mt-5 border-t pt-4">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-semibold text-slate-800">{chargesLabel}</h4>
              <Button
                size="sm"
                variant="outline"
                data-testid="dest-charge-add-btn"
                onClick={() => setSettingsForm(s => ({
                  ...s,
                  [chargesField]: [
                    ...(s[chargesField] || []),
                    { code: "", label: "", amount: 0, currency: "EUR", basis: "per_shipment" },
                  ],
                }))}
              >
                + Add charge
              </Button>
            </div>
            <p className="text-xs text-slate-500 mb-2">
              Appears at the bottom of the Calculator sheet so customers see a complete {isExport ? "EXW" : "CIF"} estimate.
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-xs border border-slate-200 rounded">
                <thead className="bg-slate-100 text-slate-700">
                  <tr>
                    <th className="px-2 py-1.5 text-left">Code</th>
                    <th className="px-2 py-1.5 text-left">Description</th>
                    <th className="px-2 py-1.5 text-right">Amount</th>
                    <th className="px-2 py-1.5 text-left">CCY</th>
                    <th className="px-2 py-1.5 text-left">Basis</th>
                    <th className="px-2 py-1.5"></th>
                  </tr>
                </thead>
                <tbody>
                  {(settingsForm[chargesField] || []).map((ch, idx) => (
                    <tr key={idx} className="border-t" data-testid={`dest-charge-row-${idx}`}>
                      <td className="px-1 py-1">
                        <Input
                          value={ch.code}
                          onChange={e => updateDestCharge(idx, "code", e.target.value)}
                          className="h-7 text-xs"
                        />
                      </td>
                      <td className="px-1 py-1">
                        <Input
                          value={ch.label}
                          onChange={e => updateDestCharge(idx, "label", e.target.value)}
                          className="h-7 text-xs"
                        />
                      </td>
                      <td className="px-1 py-1">
                        <Input
                          type="number"
                          step="0.5"
                          value={ch.amount}
                          onChange={e => updateDestCharge(idx, "amount", e.target.value)}
                          className="h-7 text-xs text-right"
                        />
                      </td>
                      <td className="px-1 py-1">
                        <select
                          value={ch.currency || "EUR"}
                          onChange={e => updateDestCharge(idx, "currency", e.target.value)}
                          className="h-7 text-xs border rounded px-1"
                        >
                          <option value="EUR">EUR</option>
                          <option value="USD">USD</option>
                        </select>
                      </td>
                      <td className="px-1 py-1">
                        <select
                          value={ch.basis || "per_shipment"}
                          onChange={e => updateDestCharge(idx, "basis", e.target.value)}
                          className="h-7 text-xs border rounded px-1"
                        >
                          <option value="per_shipment">per shipment</option>
                          <option value="per_cbm">per CBM</option>
                          <option value="per_kg">per kg</option>
                          <option value="per_set">per set</option>
                        </select>
                      </td>
                      <td className="px-1 py-1 text-right">
                        <button
                          onClick={() => removeDestCharge(idx)}
                          className="text-red-500 hover:text-red-700 text-xs"
                          data-testid={`dest-charge-remove-${idx}`}
                          title="Remove this charge"
                        >
                          ✕
                        </button>
                      </td>
                    </tr>
                  ))}
                  {(settingsForm[chargesField] || []).length === 0 && (
                    <tr><td colSpan={6} className="text-center text-slate-400 py-3 text-xs">
                      No {isExport ? "origin" : "destination"} charges configured. Click "+ Add charge".
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Filter bar */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Filter by country, port name or UN/LOCODE…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="tariff-search"
          />
        </div>
        <div className="flex items-center gap-1 text-xs">
          {[
            { id: "all", label: `All (${data.rows?.length || 0})` },
            { id: "flagged", label: `🚩 Flagged (${data.summary?.flagged || 0})` },
            { id: "overridden", label: `✏️ Overridden (${data.summary?.overridden || 0})` },
          ].map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              data-testid={`tariff-filter-${f.id}`}
              className={`px-3 py-1.5 rounded border ${
                filter === f.id
                  ? "bg-slate-900 text-white border-slate-900"
                  : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="text-center py-12 text-slate-500">Loading tariff…</div>
      ) : filteredRows.length === 0 ? (
        <div className="text-center py-12 text-slate-500" data-testid="tariff-empty">
          {data.card ? "No rows match filter." : "No Freightlinks import card uploaded yet."}
        </div>
      ) : (
        <div className="overflow-x-auto border border-slate-200 rounded-lg">
          <table className="w-full text-sm">
            <thead className="bg-slate-100 text-slate-700 text-xs">
              <tr>
                <th className="px-3 py-2 text-left">Country</th>
                <th className="px-3 py-2 text-left">{portLabel}</th>
                {!isExport && <th className="px-3 py-2 text-right">TT→SIN</th>}
                {!isExport && <th className="px-3 py-2 text-right">+ Buffer</th>}
                <th className="px-3 py-2 text-right">{isExport ? "TT (days)" : "TT→SIKOP"}</th>
                <th className="px-3 py-2 text-right">Buy (FL)</th>
                <th className="px-3 py-2 text-right">Markup</th>
                <th className="px-3 py-2 text-right">Sell USD/CBM</th>
                <th className="px-3 py-2 text-right">ECU</th>
                <th className="px-3 py-2 text-right">Shipco</th>
                <th className="px-3 py-2 text-left">Surcharges</th>
                <th className="px-3 py-2 text-left">Flag</th>
                <th className="px-3 py-2 text-left">Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((r, idx) => {
                const isEditing = editingRow === `${r.pol}|${r.pod}`;
                return (
                  <tr
                    key={`${r.pol}-${r.pod}`}
                    className={`border-t ${idx % 2 ? "bg-slate-50" : ""} ${r.flagged_for_check ? "bg-amber-50" : ""}`}
                    data-testid={`tariff-row-${r[portFieldKey]}`}
                  >
                    <td className="px-3 py-2 text-slate-700">{r.country || "—"}</td>
                    <td className="px-3 py-2 font-medium text-slate-900">{r[portNameKey]}</td>
                    {!isExport && <td className="px-3 py-2 text-right">{r.tt_to_sin ?? "—"}</td>}
                    {!isExport && <td className="px-3 py-2 text-right text-slate-500">{data.config?.transit_buffer_days}</td>}
                    <td className="px-3 py-2 text-right font-medium">{isExport ? (r.tt_days ?? "—") : (r.tt_to_sikop ?? "—")}</td>
                    <td className="px-3 py-2 text-right text-slate-600">{r.buy_rate_cbm}</td>
                    <td className="px-3 py-2 text-right">
                      <span className={r.has_override ? "text-emerald-700 font-medium" : "text-slate-600"}>
                        {r.effective_markup_usd.toFixed(2)}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right font-semibold text-slate-900">
                      {r.sell_rate_cbm}
                    </td>
                    <td className="px-3 py-2 text-right text-slate-500">
                      {r.competitors?.ecu ?? "—"}
                    </td>
                    <td className="px-3 py-2 text-right text-slate-500">
                      {r.competitors?.shipco ?? "—"}
                    </td>
                    <td className="px-3 py-2">
                      {r.flagged_for_check ? (
                        <span
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-xs"
                          title={`Cheaper at ${r.cheapest_competitor}: ${r.cheapest_competitor_rate} USD`}
                        >
                          <AlertTriangle className="h-3 w-3" />
                          {r.cheapest_competitor}
                        </span>
                      ) : r.has_override ? (
                        <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                      ) : null}
                    </td>
                    <td className="px-3 py-2">
                      {isEditing ? (
                        <div className="flex flex-col gap-1 min-w-[220px]" data-testid={`tariff-edit-row-${r[portFieldKey]}`}>
                          <Input
                            placeholder="Markup USD"
                            value={editForm.markup_usd}
                            onChange={(e) => setEditForm({ ...editForm, markup_usd: e.target.value })}
                            className="h-7 text-xs"
                          />
                          <Input
                            placeholder="Or absolute sell rate"
                            value={editForm.override_sell_rate}
                            onChange={(e) => setEditForm({ ...editForm, override_sell_rate: e.target.value })}
                            className="h-7 text-xs"
                          />
                          <div className="flex gap-1 items-center">
                            <Input
                              placeholder="Code (DGS/BAT/TRD)"
                              value={editForm.surcharge_code}
                              onChange={(e) => setEditForm({ ...editForm, surcharge_code: e.target.value })}
                              className="h-7 text-xs flex-1"
                              data-testid={`tariff-surcharge-code-${r[portFieldKey]}`}
                            />
                            <Input
                              placeholder="USD"
                              value={editForm.surcharge_amount}
                              onChange={(e) => setEditForm({ ...editForm, surcharge_amount: e.target.value })}
                              className="h-7 text-xs w-16"
                            />
                          </div>
                          <div className="flex gap-1">
                            <Button size="sm" onClick={() => saveOverride(r)} className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700">Save</Button>
                            <Button size="sm" variant="outline" onClick={() => setEditingRow(null)} className="h-7 text-xs">Cancel</Button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1 flex-wrap">
                          <button
                            onClick={() => {
                              setEditingRow(`${r.pol}|${r.pod}`);
                              setEditForm({
                                markup_usd: "", override_sell_rate: "", reason: "",
                                surcharge_code: "", surcharge_amount: "", surcharge_basis: "per_set",
                              });
                            }}
                            className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1"
                            data-testid={`tariff-edit-btn-${r[portFieldKey]}`}
                            title="Override markup or sell rate for this port"
                          >
                            <Edit3 className="h-3 w-3" /> Edit
                          </button>
                          {r.flagged_for_check && (
                            <button
                              onClick={() => matchCompetition(r)}
                              className="text-xs text-amber-700 hover:underline"
                              data-testid={`tariff-match-btn-${r[portFieldKey]}`}
                              title={`Match ${r.cheapest_competitor} rate: ${r.cheapest_competitor_rate}`}
                            >
                              Match
                            </button>
                          )}
                          {r.has_override && (
                            <button
                              onClick={() => clearOverride(r)}
                              className="text-xs text-slate-500 hover:underline inline-flex items-center gap-0.5"
                              data-testid={`tariff-clear-btn-${r[portFieldKey]}`}
                              title="Clear override and revert to global markup"
                            >
                              <X className="h-3 w-3" /> Clear
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
